import React from 'react';
import { Badge } from '../ui/badge';
import { 
  Monitor, 
  CalendarDays, 
  Bot, 
  Clock, 
  TrendingUp,
  CheckCircle
} from 'lucide-react';

const VALUE_PROPS = [
  {
    icon: Monitor,
    title: 'IT-Specific Context',
    description: 'Examples from tech environments, not generic business theory. Real scenarios from dev teams, infrastructure projects, and stakeholder management.',
    highlight: 'Built for tech leaders'
  },
  {
    icon: CalendarDays,
    title: 'Structured 30-Day Cycles',
    description: 'Each book is a focused 30-day module. Complete one, apply what you learned, then progress. No endless content treadmill.',
    highlight: '30 days per module'
  },
  {
    icon: Bot,
    title: 'AI-Powered Reinforcement',
    description: 'The Managerial Companion helps you apply concepts to real situations. Reflect on your day, get contextual guidance, build leadership habits.',
    highlight: 'Personal AI coach'
  },
  {
    icon: Clock,
    title: '20-Minute Daily Focus',
    description: 'Designed for time-poor managers. Quick morning reads, short evening reflections. Leadership development that fits your schedule.',
    highlight: '~20 min/day'
  },
  {
    icon: TrendingUp,
    title: 'Measurable Outcomes',
    description: 'Track your growth, not just consumption. See patterns in your reflections, identify growth areas, and measure real behavioral change.',
    highlight: 'Track your progress'
  }
];

const WhyItWorks = ({ series }) => {
  const accentColor = series?.accentColor || '#DC2626';

  return (
    <section className="py-24 bg-stone-50 relative overflow-hidden" data-testid="why-it-works-section">
      {/* Top accent line */}
      <div 
        className="absolute top-0 left-0 w-full h-1"
        style={{ background: `linear-gradient(to right, transparent, ${accentColor}, transparent)` }}
      />

      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-white border border-stone-200 text-stone-600 shadow-sm">
            Why This Works
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Built for <span style={{ color: accentColor }}>IT Managers</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Not another generic leadership program. This system is engineered specifically for technical leaders.
          </p>
        </div>

        {/* Value Props Grid */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUE_PROPS.map((prop, index) => {
            const Icon = prop.icon;
            return (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-stone-100 hover:border-stone-200"
                data-testid={`value-prop-${index}`}
              >
                {/* Icon & Highlight */}
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300"
                    style={{ backgroundColor: `${accentColor}10` }}
                  >
                    <Icon 
                      className="w-6 h-6" 
                      style={{ color: accentColor }}
                    />
                  </div>
                  <span 
                    className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ 
                      backgroundColor: `${accentColor}10`,
                      color: accentColor
                    }}
                  >
                    {prop.highlight}
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {prop.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {prop.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom reinforcement */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-6 flex-wrap justify-center text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>No fluff</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>No endless modules</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>Just structured growth</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyItWorks;
